import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/tracing/grid/table';
import {TracingGridFormattedRow, TracingGridRowRaw} from 'types';

import {isOperator$} from '../../../../models/auth';

interface Props {
    configFunc: (t: TFunction, isOperator: boolean) => Array<any>;
    dataMapper: (
        data: TracingGridRowRaw[] | undefined,
        search: string,
        t: TFunction,
        isOperator: boolean,
    ) => TracingGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const TracingGridTable = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, filters, sortings, dataLoading} = useStore(tableState$);
    const isOperator = useStore(isOperator$);
    const location = useLocation();
    useGate(Gate);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t, !!isOperator),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings, isOperator],
    );
    const mappedData = React.useMemo(
        () => dataMapper(data?.content, location.search, t, !!isOperator),
        [dataMapper, data, location, t, isOperator],
    );

    return (
        <>
            <Table
                columns={columns}
                data={mappedData}
                // TotalCount from backed - the remaining number of elements to the end of the list
                // totalRowsCount={data?.totalCount}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                hasNext={data?.hasNext}
                isLoading={dataLoading}
                headerControls={headerControls}
                userConfig={{
                    gridName: 'tracing',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                exportConfig={exportConfig}
            />
        </>
    );
};
